import React, { useState } from 'react';
import { Navbar, Container, Nav, Button, Image, Tooltip, OverlayTrigger, Popover } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  IoExitOutline, 
  IoPersonOutline, 
  IoSwapHorizontalOutline, 
  IoDocumentTextOutline,
  IoChevronDownOutline,
  IoLockClosedOutline,
  IoGitBranchOutline,
  IoStatsChartOutline,
  IoCodeSlashOutline
} from 'react-icons/io5';
import NotificationBell from './notifications/NotificationBell';
import '../styles/Notifications.css';

// Logo styling
const logoStyle = {
  height: '40px',
  objectFit: 'contain',
  transform: 'scale(3.3)'
};

// OPTION 1: Clean white with subtle shadow
const navbarStyleOption1 = {
  position: 'relative',
  zIndex: 2000,
  pointerEvents: 'auto',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  padding: '0.8rem 0',
  background: 'white',
  borderBottom: '1px solid #eaeaea'
};

// OPTION 2: Light gray with very subtle gradient
const navbarStyleOption2 = {
  position: 'relative',
  zIndex: 2000,
  pointerEvents: 'auto',
  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  padding: '0.8rem 0',
  background: 'linear-gradient(180deg, #f8f9fa, #f1f3f5)'
};

// OPTION 3: Subtle blue-gray
const navbarStyleOption3 = {
  position: 'relative',
  zIndex: 2000,
  pointerEvents: 'auto',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  padding: '0.8rem 0',
  background: '#f5f7fa'
};

// OPTION 4: Almost transparent with blur effect
const navbarStyleOption4 = {
  position: 'relative',
  zIndex: 2000,
  pointerEvents: 'auto',
  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  padding: '0.8rem 0',
  background: 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(0,0,0,0.05)'
};

// OPTION 5: Minimal border-only
const navbarStyleOption5 = {
  position: 'relative',
  zIndex: 2000,
  pointerEvents: 'auto',
  padding: '0.8rem 0',
  background: 'white',
  borderBottom: '2px solid #e9ecef'
};

// Icon button style - dark for light backgrounds
const iconButtonStyleForLight = {
  background: 'transparent',
  border: 'none',
  color: '#495057',
  padding: '0.5rem',
  fontSize: '1.2rem',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  margin: '0 0.3rem'
};

const iconButtonHoverStyleForLight = {
  ...iconButtonStyleForLight,
  background: 'rgba(0,0,0,0.05)',
  color: '#212529',
  transform: 'translateY(-2px)'
};

// Brand style for light background
const brandStyleForLight = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  transition: 'transform 0.2s ease'
};

// User profile container style
const userProfileStyle = {
  display: 'flex',
  alignItems: 'center',
  marginLeft: '0.3rem'
};

// Admin badge style
const adminBadgeStyle = {
  fontSize: '0.65rem',
  padding: '0.15rem 0.35rem',
  marginLeft: '0.3rem'
};

const Navigation = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isDeveloper = user?.role === 'developer';
  const isAdminOrDeveloper = isSuperAdmin || isDeveloper;
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // Choose which style to use (CHANGE THIS NUMBER TO TRY DIFFERENT OPTIONS: 1-5)
  const activeStyleOption = 1;
  
  // Set the active navbar style based on the option
  const navbarStyle = 
    activeStyleOption === 1 ? navbarStyleOption1 :
    activeStyleOption === 2 ? navbarStyleOption2 :
    activeStyleOption === 3 ? navbarStyleOption3 :
    activeStyleOption === 4 ? navbarStyleOption4 :
    navbarStyleOption5;
  
  // Use appropriate icon and brand styles based on background
  const iconButtonStyle = iconButtonStyleForLight;
  const iconButtonHoverStyle = iconButtonHoverStyleForLight;
  const brandStyle = brandStyleForLight;
  
  // Navigation handlers
  const goToHome = () => {
    navigate('/');
  };

  const goToFieldMappings = () => {
    navigate('/field-mappings');
  };

  const goToBankDataMappings = () => {
    navigate('/bankdata-mappings');
  };

  const goToAdvancedCriteria = () => {
    navigate('/lender-advanced-criteria');
  };

  const goToLenderSettings = () => {
    navigate('/lender-settings');
  };

  const goToUserSettings = () => {
    navigate('/user-settings');
  };

  const goToApiDocs = () => {
    window.open('/api/v1/external/docs', '_blank', 'noopener,noreferrer');
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
  };

  // Tooltip rendering
  const renderTooltip = (text) => (props) => (
    <Tooltip id={`tooltip-${text.toLowerCase().replace(/\s/g, '-')}`} className="navbar-tooltip" {...props}>
      {text}
    </Tooltip>
  );

  // User popover content
  const userPopover = (
    <Popover id="user-popover">
      <Popover.Body className="p-0">
        <div className="p-3 border-bottom">
          <div className="fw-bold">{user?.displayName || user?.email}</div>
          <div className="text-muted small">
            {user?.role === 'super_admin' ? 'Administrator' : 
             user?.role === 'developer' ? 'Developer' : 
             user?.role === 'admin' ? 'Admin' : 'User'}
          </div>
        </div>
        <div className="p-2">
          <Button 
            variant="link" 
            className="text-danger w-100 text-start p-2 d-flex align-items-center" 
            onClick={handleLogout}
          >
            <IoExitOutline className="me-2" /> Logout
          </Button>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <Navbar expand="lg" style={navbarStyle}>
      <Container className="d-flex justify-content-between align-items-center">
        {/* Left side - Brand */}
        <Navbar.Brand 
          onClick={goToHome} 
          style={brandStyle}
          className="brand-logo"
          onMouseEnter={(e) => {e.currentTarget.style.transform = 'scale(1.05)'}}
          onMouseLeave={(e) => {e.currentTarget.style.transform = 'scale(1)'}}
        >
          <Image src="/subportallogo.png" alt="BTS Logo" style={logoStyle} />
        </Navbar.Brand>
        
        {isAuthenticated && (
          /* Right side - Icons */
          <Nav className="d-flex align-items-center">
            {/* Submit Deal Icon */}
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 150, hide: 250 }}
              overlay={renderTooltip('Submit Deal')}
            >
              <Button 
                style={iconButtonStyle} 
                className="icon-btn"
                onClick={goToHome}
              >
                <IoDocumentTextOutline />
              </Button>
            </OverlayTrigger>
            
            {/* Field Mappings Icon (Admin or Developer) */}
            {isAdminOrDeveloper && (
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 150, hide: 250 }}
                overlay={renderTooltip('Field Mappings')}
              >
                <Button 
                  style={iconButtonStyle} 
                  className="icon-btn"
                  onClick={goToFieldMappings}
                >
                  <IoSwapHorizontalOutline />
                </Button>
              </OverlayTrigger>
            )}
            
            {/* Bank Data Mappings Icon (Admin or Developer) */}
            {isAdminOrDeveloper && (
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 150, hide: 250 }}
                overlay={renderTooltip('Bank Data Mappings')}
              >
                <Button 
                  style={iconButtonStyle} 
                  className="icon-btn"
                  onClick={goToBankDataMappings}
                >
                  <IoStatsChartOutline />
                </Button>
              </OverlayTrigger>
            )}
            
            {/* Advanced Criteria Icon (Admin or Developer) - Replaces old Lender Match */}
            {isAdminOrDeveloper && (
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 150, hide: 250 }}
                overlay={renderTooltip('Advanced Criteria')}
              >
                <Button 
                  style={iconButtonStyle} 
                  className="icon-btn"
                  onClick={goToAdvancedCriteria}
                >
                  <IoGitBranchOutline />
                </Button>
              </OverlayTrigger>
            )}
            
            {/* Lender Settings Icon (Admin or Developer) */}
            {isAdminOrDeveloper && (
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 150, hide: 250 }}
                overlay={renderTooltip('Lender Settings')}
              >
                <Button 
                  style={iconButtonStyle} 
                  className="icon-btn"
                  onClick={goToLenderSettings}
                >
                  <IoLockClosedOutline />
                </Button>
              </OverlayTrigger>
            )}
            
            {/* External API Docs (Super Admin or Developer) */}
            {isAdminOrDeveloper && (
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 150, hide: 250 }}
                overlay={renderTooltip('External API Docs')}
              >
                <Button 
                  style={iconButtonStyle} 
                  className="icon-btn"
                  onClick={goToApiDocs}
                >
                  <IoCodeSlashOutline />
                </Button>
              </OverlayTrigger>
            )}

            {/* User Settings Icon (Developer) */}
            {isDeveloper && (
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 150, hide: 250 }}
                overlay={renderTooltip('User Settings')}
              >
                <Button 
                  style={iconButtonStyle} 
                  className="icon-btn"
                  onClick={goToUserSettings}
                >
                  <IoPersonOutline />
                </Button>
              </OverlayTrigger>
            )}
            
            {/* Notification Bell */}
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 150, hide: 250 }}
              overlay={renderTooltip('Notifications')}
            >
              <div>
                <NotificationBell 
                  iconButtonStyle={iconButtonStyle} 
                  iconButtonHoverStyle={iconButtonHoverStyle}
                />
              </div>
            </OverlayTrigger>
            
            {/* User Menu */}
            <OverlayTrigger
              trigger="click"
              placement="bottom"
              show={userMenuOpen}
              onToggle={(show) => setUserMenuOpen(show)}
              overlay={userPopover}
              rootClose
            >
              <Button 
                style={{...iconButtonStyle, marginLeft: '0.5rem'}} 
                className="icon-btn user-icon-btn d-flex align-items-center"
              >
                <IoPersonOutline className="me-1" />
                <IoChevronDownOutline style={{ fontSize: '0.8rem' }} />
              </Button>
            </OverlayTrigger>
          </Nav>
        )}
      </Container>
    </Navbar>
  );
};

export default Navigation; 